import { useState } from "react";
import { Check, Lock, ChevronDown, ChevronUp } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

// =============================================
// Pipeline step definitions grouped by phase
// =============================================

export interface PipelineStep {
  key: string;
  label: string;
  phase: "map" | "knowledge" | "existing" | "validation" | "profile" | "content" | "sro_structure" | "backend_improvement" | "qa" | "presentation" | "migration";
  optional?: boolean;
  /** Klucze poprzednich kroków które muszą być done żeby ten był odblokowany */
  requires?: string[];
}

export const PIPELINE_STEPS: PipelineStep[] = [
  // Phase 1: Map Strategy
  { key: "macro", label: "Macro", phase: "map" },
  { key: "sbd", label: "SBD", phase: "map", requires: ["macro"] },
  { key: "seeds", label: "Seeds", phase: "map", requires: ["macro"] },
  { key: "nodes", label: "Nodes", phase: "map", requires: ["seeds"] },
  // Phase 2: Knowledge
  { key: "entities", label: "Encje", phase: "knowledge", requires: ["nodes"] },
  { key: "eav", label: "EAV", phase: "knowledge", requires: ["entities"] },
  { key: "relations", label: "Relacje", phase: "knowledge", requires: ["nodes"] },
  // Phase 2b: Existing Posts (optional)
  { key: "existing-posts", label: "Istniejące", phase: "existing", optional: true },
  // Phase 3: Validation
  { key: "audit", label: "Audyt", phase: "validation", requires: ["nodes"] },
  // Phase 3b: Profile
  { key: "profile", label: "Profil", phase: "profile", requires: ["macro"] },
  // Phase 4: Content (Treść → SEO meta → Publikacja → Sprawdzanie)
  { key: "content", label: "Treść", phase: "content", requires: ["nodes"] },
  { key: "seo-meta", label: "SEO meta", phase: "content", requires: ["content"] },
  { key: "publish", label: "Publikacja", phase: "content", requires: ["content"] },
  { key: "qa-check", label: "Sprawdzanie", phase: "content", requires: ["publish"] },
  // Indexer workflow: URL-e opublikowanych → kopiuj/CSV → oznacz dodane do indexu
  { key: "indexing", label: "Indeksowanie", phase: "content", optional: true, requires: ["publish"] },
  // Phase 5: Struktura SRO (A1 Pillar, optional, gated per-mapa flagą)
  // USUNIETE 2026-07-21: "cluster-coverage" (A2) + "entity-salience" (A4) — patrz CHANGELOG.
  { key: "pillar", label: "Pillar Page", phase: "sro_structure", optional: true, requires: ["publish"] },
  // Phase 5b: Ulepszanie zaplecza — Strona autora + Podstrony + Autor info + Logo/favicon (optional, gated flagą)
  { key: "author-page", label: "Strona autora", phase: "backend_improvement", optional: true, requires: ["profile"] },
  { key: "unique-pages", label: "Podstrony", phase: "backend_improvement", optional: true },
  { key: "author-info", label: "Autor info", phase: "backend_improvement", optional: true },
  { key: "brand-logo", label: "Logo i favicon", phase: "backend_improvement", optional: true },
  // Phase 6: Prezentacja — read-only podgląd całej mapy dla klienta
  { key: "presentation", label: "Prezentacja mapy", phase: "presentation", optional: true },
  // Phase 7: Migracja WP -> strona statyczna (Cloudflare Pages), te same URL-e (2026-09-21)
  { key: "migration", label: "Migracja", phase: "migration", optional: true, requires: ["publish"] },
];

const PHASE_LABELS: Record<string, string> = {
  map: "Strategia mapy",
  knowledge: "Wiedza",
  existing: "Istniejące wpisy",
  validation: "Walidacja",
  profile: "Profil",
  content: "Treść",
  sro_structure: "Struktura SRO",
  backend_improvement: "Ulepszanie zaplecza",
  qa: "Sprawdzanie",
  presentation: "Prezentacja",
  migration: "Migracja",
};

export const PHASE_COLORS: Record<string, { text: string; bg: string; ring: string; border: string; connector: string }> = {
  map: { text: "text-blue-500", bg: "bg-blue-500/10", ring: "ring-blue-500/30", border: "border-blue-500/25", connector: "bg-blue-500/30" },
  knowledge: { text: "text-purple-500", bg: "bg-purple-500/10", ring: "ring-purple-500/30", border: "border-purple-500/25", connector: "bg-purple-500/30" },
  existing: { text: "text-indigo-500", bg: "bg-indigo-500/10", ring: "ring-indigo-500/30", border: "border-indigo-500/25", connector: "bg-indigo-500/30" },
  validation: { text: "text-amber-500", bg: "bg-amber-500/10", ring: "ring-amber-500/30", border: "border-amber-500/25", connector: "bg-amber-500/30" },
  profile: { text: "text-pink-500", bg: "bg-pink-500/10", ring: "ring-pink-500/30", border: "border-pink-500/25", connector: "bg-pink-500/30" },
  content: { text: "text-emerald-500", bg: "bg-emerald-500/10", ring: "ring-emerald-500/30", border: "border-emerald-500/25", connector: "bg-emerald-500/30" },
  sro_structure: { text: "text-violet-500", bg: "bg-violet-500/10", ring: "ring-violet-500/30", border: "border-violet-500/25", connector: "bg-violet-500/30" },
  backend_improvement: { text: "text-rose-500", bg: "bg-rose-500/10", ring: "ring-rose-500/30", border: "border-rose-500/25", connector: "bg-rose-500/30" },
  qa: { text: "text-teal-500", bg: "bg-teal-500/10", ring: "ring-teal-500/30", border: "border-teal-500/25", connector: "bg-teal-500/30" },
  presentation: { text: "text-fuchsia-500", bg: "bg-fuchsia-500/10", ring: "ring-fuchsia-500/30", border: "border-fuchsia-500/25", connector: "bg-fuchsia-500/30" },
  migration: { text: "text-orange-500", bg: "bg-orange-500/10", ring: "ring-orange-500/30", border: "border-orange-500/25", connector: "bg-orange-500/30" },
};

// =============================================
// Props
// =============================================

export interface PipelineProgressProps {
  completedSteps: Record<string, boolean>;
  activeStep: string;
  onStepClick: (stepKey: string) => void;
  /** Per-step summary data — np. {seeds: 19, nodes: 159, audit_score: 78} */
  summary?: Record<string, string | number | null>;
  /** Filter visible phases — stare mapy bez SRO nie widza fazy 'sro_structure'.
   * Default: wszystkie 7 faz. P3 z planu (MF-6) — MapDetailPage powinien
   * propsowac filter na podstawie map.pillar_pages_enabled / author_page_enabled. */
  visiblePhases?: readonly string[];
}

// =============================================
// Helpers
// =============================================

export function isStepLocked(step: PipelineStep, completedSteps: Record<string, boolean>): {
  locked: boolean;
  missingRequires: string[];
} {
  if (!step.requires || step.requires.length === 0) return { locked: false, missingRequires: [] };
  const missing = step.requires.filter((req) => !completedSteps[req]);
  return { locked: missing.length > 0, missingRequires: missing };
}

export function findNextRecommendedStep(
  steps: PipelineStep[],
  completedSteps: Record<string, boolean>,
  activeStep: string,
): PipelineStep | null {
  // First: any non-optional step that is unlocked but not done
  for (const step of steps) {
    if (step.optional) continue;
    if (completedSteps[step.key]) continue;
    if (step.key === activeStep) continue;
    const { locked } = isStepLocked(step, completedSteps);
    if (!locked) return step;
  }
  // Fallback: next step in sequence
  const idx = steps.findIndex((s) => s.key === activeStep);
  if (idx >= 0 && idx < steps.length - 1) return steps[idx + 1];
  return null;
}

// =============================================
// Component
// =============================================

const COLLAPSE_KEY = "pipeline_collapsed";

function readCollapsed(): boolean {
  try { return localStorage.getItem(COLLAPSE_KEY) === "true"; } catch { return false; }
}

export function PipelineProgress({
  completedSteps,
  activeStep,
  onStepClick,
  summary = {},
  visiblePhases,
}: PipelineProgressProps) {
  const activeIndex = PIPELINE_STEPS.findIndex((s) => s.key === activeStep);
  const activeStepDef = PIPELINE_STEPS[activeIndex];
  // Zwijany do jednej linii (2026-09-22): stan globalny w localStorage, domyslnie rozwiniety
  const [collapsed, setCollapsed] = useState<boolean>(readCollapsed);
  const toggleCollapsed = () => {
    setCollapsed((c) => {
      try { localStorage.setItem(COLLAPSE_KEY, String(!c)); } catch { /* prywatny tryb */ }
      return !c;
    });
  };

  // Required steps only (excluding 'existing-posts' which is optional)
  const requiredSteps = PIPELINE_STEPS.filter((s) => !s.optional);
  const doneRequiredCount = requiredSteps.filter((s) => completedSteps[s.key]).length;
  const overallPct = Math.round((doneRequiredCount / requiredSteps.length) * 100);

  // P3 (MF-6 z planu): visiblePhases filtruje fazy. Default = wszystkie 7. Stare mapy bez SRO
  // dostana visiblePhases bez 'sro_structure' — propsowane z MapDetailPage.
  const phases: readonly string[] = visiblePhases ?? [
    "map", "knowledge", "existing", "validation", "profile", "content", "sro_structure",
  ];

  return (
    <TooltipProvider delayDuration={300}>
      <div className="mb-6 space-y-4 sticky top-0 bg-background/95 backdrop-blur-sm z-30 pt-2 pb-4 -mx-6 px-6 border-b border-border/40">
        {/* Top progress bar — overall completion */}
        <div className="space-y-1.5">
          <div className="flex items-center justify-between text-xs">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-foreground text-sm">Postęp mapy</span>
              <Badge
                variant="outline"
                className={`font-mono text-[11px] py-0.5 px-2 ${
                  overallPct === 100
                    ? "border-emerald-500/40 text-emerald-500 bg-emerald-500/10"
                    : overallPct >= 50
                      ? "border-amber-500/40 text-amber-500 bg-amber-500/10"
                      : "border-blue-500/40 text-blue-500 bg-blue-500/10"
                }`}
              >
                {doneRequiredCount}/{requiredSteps.length}
              </Badge>
              {activeStepDef && (
                <span className="text-muted-foreground text-[11px]">
                  · aktualny:{" "}
                  <span className={`font-semibold ${PHASE_COLORS[activeStepDef.phase].text}`}>
                    {activeStepDef.label}
                  </span>
                </span>
              )}
            </div>
            <span className="flex items-center gap-2">
              <span className="font-mono font-bold text-foreground text-sm">{overallPct}%</span>
              <button
                type="button"
                onClick={toggleCollapsed}
                className="rounded-md p-1 text-muted-foreground hover:bg-muted/40 hover:text-foreground"
                title={collapsed ? "Rozwiń fazy" : "Zwiń do jednej linii"}
                aria-expanded={!collapsed}
              >
                {collapsed ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
              </button>
            </span>
          </div>
          <div className={`${collapsed ? "h-1" : "h-2.5"} bg-muted/40 rounded-full overflow-hidden ring-1 ring-border/30`}>
            <div
              className="h-full bg-gradient-to-r from-blue-500 via-purple-500 to-emerald-500 rounded-full transition-all duration-700 ease-out shadow-sm"
              style={{ width: `${overallPct}%` }}
            />
          </div>
        </div>

        {/* Phase rows — STOP-10 (2026-06-01): flex-wrap kapsuły zamiast grid-cols-6.
            Szeroko → 1 rząd, na laptopie składa się 2-3 rzędy, nigdy nie scrolluje w bok.
            Zwiniety stepper (2026-09-22) chowa kapsuly — zostaje jedna linia z paskiem. */}
        <div className={collapsed ? "hidden" : "flex flex-wrap gap-2"}>
          {phases.map((phase) => {
            const phaseSteps = PIPELINE_STEPS.filter((s) => s.phase === phase);
            const phaseCompleted = phaseSteps.filter((s) => completedSteps[s.key]).length;
            const phaseColor = PHASE_COLORS[phase];
            const phaseDone = phaseCompleted === phaseSteps.length;
            const phaseInProgress = phaseSteps.some((s) => s.key === activeStep);
            const phaseEmpty = phaseCompleted === 0 && !phaseInProgress;

            return (
              <div
                key={phase}
                className={`flex-1 basis-auto flex flex-col gap-1.5 rounded-[10px] border px-2.5 py-2 transition-colors ${
                  phaseInProgress
                    ? `${phaseColor.bg} ${phaseColor.border} ring-1 ${phaseColor.ring}`
                    : phaseDone
                      ? "bg-emerald-500/[0.04] border-emerald-500/25"
                      : phaseEmpty
                        ? "bg-muted/[0.06] border-dashed border-border/30 opacity-75"
                        : `bg-muted/15 ${phaseColor.border}`
                }`}
              >
                {/* Phase header — nazwa fazy + licznik */}
                <div className="flex items-center justify-between gap-2">
                  <p className={`text-[13px] font-semibold uppercase tracking-wide ${phaseColor.text}`}>
                    {PHASE_LABELS[phase]}
                  </p>
                  <span className="text-[12px] font-mono tabular-nums text-muted-foreground">
                    {phaseCompleted}/{phaseSteps.length}
                  </span>
                </div>

                {/* Steps row */}
                <div className="flex items-start justify-center gap-1">
                  {phaseSteps.map((step, i) => {
                    const isDone = completedSteps[step.key];
                    const isActive = step.key === activeStep;
                    const { locked, missingRequires } = isStepLocked(step, completedSteps);
                    const summaryVal = summary[step.key];
                    const hasValue = summaryVal != null && summaryVal !== "";

                    const button = (
                      <button
                        key={step.key}
                        onClick={() => onStepClick(step.key)}
                        disabled={locked}
                        className={`
                          group flex flex-col items-center gap-[3px] min-w-[36px] px-1 py-1 rounded-md transition-all
                          focus:outline-none focus:ring-2 focus:ring-primary/40
                          disabled:opacity-50 disabled:cursor-not-allowed
                          ${isActive ? "" : "hover:bg-muted/40"}
                          ${locked ? "" : "cursor-pointer"}
                        `}
                      >
                        {/* Kropka 17px */}
                        <div
                          className={`
                            relative flex items-center justify-center w-[17px] h-[17px] rounded-full transition-all
                            ${
                              isDone
                                ? "bg-emerald-500 text-white"
                                : isActive
                                  ? `${phaseColor.bg} ring-4 ${phaseColor.ring}`
                                  : locked
                                    ? "bg-muted/40"
                                    : step.optional && !hasValue
                                      ? "border-[1.5px] border-dashed border-border/50"
                                      : "border-[1.5px] border-border/60"
                            }
                          `}
                        >
                          {isDone ? (
                            <Check className="h-2.5 w-2.5" strokeWidth={3} />
                          ) : locked ? (
                            <Lock className="h-2.5 w-2.5 text-muted-foreground/50" />
                          ) : isActive ? (
                            <span className="w-1 h-1 rounded-full bg-white" />
                          ) : null}
                        </div>

                        {/* Nazwa kroku */}
                        <span
                          className={`text-[13px] leading-tight whitespace-nowrap ${
                            isDone
                              ? "text-emerald-500"
                              : isActive
                                ? phaseColor.text
                                : locked
                                  ? "text-muted-foreground/50"
                                  : "text-muted-foreground"
                          }`}
                        >
                          {step.label}
                        </span>

                        {/* Wartość */}
                        <span
                          className={`font-mono text-[13px] leading-none tabular-nums ${
                            isDone
                              ? "text-emerald-600/90"
                              : isActive
                                ? phaseColor.text
                                : locked
                                  ? "text-muted-foreground/40"
                                  : "text-muted-foreground/70"
                          }`}
                        >
                          {hasValue ? summaryVal : isDone ? "ok" : step.optional ? "BRAK" : locked ? "—" : "·"}
                        </span>
                      </button>
                    );

                    // Dekoracyjny łącznik między krokami (nie po ostatnim)
                    const connector =
                      i < phaseSteps.length - 1 ? (
                        <span
                          key={`${step.key}-conn`}
                          className={`self-start mt-2 w-1.5 h-0.5 ${phaseColor.connector} opacity-50 rounded-full`}
                          aria-hidden="true"
                        />
                      ) : null;

                    if (locked && missingRequires.length > 0) {
                      const reqLabels = missingRequires
                        .map((k) => PIPELINE_STEPS.find((s) => s.key === k)?.label || k)
                        .join(", ");
                      return (
                        <Tooltip key={step.key}>
                          <TooltipTrigger asChild>
                            <div className="flex items-start">
                              {button}
                              {connector}
                            </div>
                          </TooltipTrigger>
                          <TooltipContent side="bottom" className="max-w-[220px]">
                            <p className="text-xs">
                              <span className="font-semibold">Zablokowany.</span> Najpierw ukończ:{" "}
                              <span className="font-semibold">{reqLabels}</span>
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      );
                    }
                    return (
                      <div key={step.key} className="flex items-start">
                        {button}
                        {connector}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

      </div>
    </TooltipProvider>
  );
}
